// Lecture et écriture de fichiers Excel (.xlsx) dans le navigateur.
import readExcelFile, { readSheet } from 'read-excel-file/browser';
import writeExcelFile from 'write-excel-file/browser';

/** Première feuille du fichier → tableau de lignes (valeurs : texte, nombre, booléen ou Date). */
export async function lireExcel(file) {
  if (!/\.xlsx$/i.test(file?.name || '')) throw new Error('Choisissez un fichier Excel .xlsx (dans Excel : Fichier → Enregistrer sous → Classeur Excel .xlsx)');
  try {
    return (await readSheet(file)).map((r) => r.map((c) => (typeof c === 'string' ? c.trim() : c)));
  } catch (e) {
    throw new Error(`Fichier illisible : ${e.message}`);
  }
}

/** Tous les onglets du fichier → [{ feuille, lignes }]. */
export async function lireClasseur(file) {
  if (!/\.xlsx$/i.test(file?.name || '')) throw new Error('Choisissez un fichier Excel .xlsx (dans Excel : Fichier → Enregistrer sous → Classeur Excel .xlsx)');
  try {
    return (await readExcelFile(file)).map(({ sheet, data }) => ({ feuille: sheet, lignes: data.map((r) => r.map((c) => (typeof c === 'string' ? c.trim() : c))) }));
  } catch (e) {
    throw new Error(`Fichier illisible : ${e.message}`);
  }
}

/** Télécharge un fichier .xlsx construit à partir des lignes. */
export async function ecrireExcel(lignes, nomFichier, { largeurs = [] } = {}) {
  await writeExcelFile(lignes, { columns: largeurs.map((w) => ({ width: w })), dateFormat: 'dd/mm/yyyy', fontFamily: 'Calibri', fontSize: 11 }).toFile(nomFichier);
}

const pad = (n) => String(n).padStart(2, '0');
const isoUTC = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

/** Valeur de cellule → « AAAA-MM-JJ » ; accepte une date Excel, « 28/10/2026 », « 28-10-26 », « 2026-10-28 » ou un n° de série Excel. */
export function enDate(v) {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : isoUTC(new Date(v.getTime() + 12 * 3600000)); // tolère un décalage horaire
  if (typeof v === 'number' && v > 20000 && v < 80000) return isoUTC(new Date(Date.UTC(1899, 11, 30) + Math.round(v) * 86400000));
  const s = String(v).trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (m) return `${m[1]}-${pad(m[2])}-${pad(m[3])}`;
  m = /(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/.exec(s);
  if (m) { const y = m[3].length === 2 ? `20${m[3]}` : m[3]; const d = new Date(`${y}-${pad(m[2])}-${pad(m[1])}T00:00:00Z`); return Number.isNaN(d.getTime()) ? null : isoUTC(d); }
  return null;
}

/** Valeur de cellule → « HH:MM » ou null ; accepte une heure Excel, « 8h30 », « 08:30 » ou une fraction de jour. */
export function enHeure(v) {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return `${pad(v.getUTCHours())}:${pad(v.getUTCMinutes())}`;
  if (typeof v === 'number' && v >= 0 && v < 1) { const min = Math.round(v * 1440); return `${pad(Math.floor(min / 60) % 24)}:${pad(min % 60)}`; }
  const m = /^(\d{1,2})\s*[:hH]\s*(\d{0,2})/.exec(String(v).trim());
  if (!m || Number(m[1]) > 23 || Number(m[2] || 0) > 59) return null;
  return `${pad(m[1])}:${pad(m[2] || 0)}`;
}

/** Texte comparable : minuscules, sans accents ni espaces superflus. */
export const norme = (v) => String(v ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

/** Quantité entière positive ; null si la cellule est vide ; NaN si invalide. */
export function enEntier(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[\s  ]/g, '').replace(',', '.'));
  return Number.isInteger(n) && n >= 0 ? n : NaN;
}
